import type { ReactNode } from 'react';

import { Icon } from '#/components/Icon/Icon';
import { Text } from '#/components/Text/Text';
import { VisuallyHidden } from '#/components/VisuallyHidden/VisuallyHidden';

import { FieldErrorContainer } from './FieldErrorContainer';

type Props = {
  errorMessage: ReactNode;
  /**
   * Optional label that provides additional context for screen readers.
   * This label is visually hidden but announced before the error message
   * to help screen reader users understand which field the error relates to.
   */
  visuallyHiddenLabel?: ReactNode;
  inlineLabel?: boolean;
  noWrap?: boolean;
  noMargin?: boolean;
  inputId?: string;
};

export function FieldError({
  errorMessage,
  visuallyHiddenLabel,
  inlineLabel,
  noWrap,
  noMargin = false,
  inputId,
}: Props) {
  const errorId = inputId ? `${inputId}-error` : undefined;

  return (
    <FieldErrorContainer
      $noWrap={noWrap}
      $inlineLabel={inlineLabel}
      $noMargin={noMargin}
      id={errorId}
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
    >
      <Icon name="error" aria-hidden="true" size={24} />
      {visuallyHiddenLabel && (
        <VisuallyHidden>{visuallyHiddenLabel}: </VisuallyHidden>
      )}
      <Text pl="6px" qaId={errorId}>
        {errorMessage}
      </Text>
    </FieldErrorContainer>
  );
}
